import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useEmployees } from '../hooks/use-employees';
import { useCompany } from '@/features/settings/hooks/use-company';
import { SearchBar } from '@/components/molecules/search-bar';
import { EmptyState } from '@/components/organisms/empty-state';
import { Skeleton } from '@/components/atoms/skeleton';
import { Button } from '@/components/atoms/button';
import { Card, CardContent } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/atoms/avatar';
import { ResponsiveDataList, ColumnDef } from '@/components/organisms/responsive-data-list';
import { useTranslation } from 'react-i18next';
import { Plus, Mail, Phone } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import type { EmployeeStatus, EmployeeRole, Employee } from '../types/employees.types';

const statusVariantMap: Record<EmployeeStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  active: 'default',
  inactive: 'secondary',
  pending: 'outline',
};

const roleColors: Record<EmployeeRole, string> = {
  manager: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  cashier: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  inventory: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  support: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
};

export function EmployeeListContainer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = React.useState('');

  const { data: company, isLoading: companyLoading } = useCompany();
  const companyId = company?.id ?? company?.refId ?? '';
  const { data: employeesData, isLoading, error } = useEmployees(companyId, { search });
  const employees = employeesData?.employees ?? [];

  const handleRowClick = (employee: Employee) => {
    navigate(`/employees/${employee.id}`);
  };

  // Define table columns for desktop view
  const columns: ColumnDef<Employee>[] = React.useMemo(
    () => [
      {
        id: 'avatar',
        header: '',
        cell: (employee) => (
          <Avatar className="size-10">
            <AvatarImage src={employee.avatar} />
            <AvatarFallback>
              {employee.firstName[0]}
              {employee.lastName[0]}
            </AvatarFallback>
          </Avatar>
        ),
        className: 'w-16',
      },
      {
        id: 'name',
        header: t('employees.name'),
        cell: (employee) => (
          <div>
            <p className="font-medium">{employee.firstName} {employee.lastName}</p>
            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${roleColors[employee.role]}`}>
              {t(`employees.roles.${employee.role}`)}
            </span>
          </div>
        ),
        sortable: true,
      },
      {
        id: 'email',
        header: t('employees.email'),
        cell: (employee) => (
          <span className="flex items-center gap-2 text-muted-foreground">
            <Mail className="size-4" />
            {employee.email}
          </span>
        ),
      },
      {
        id: 'phone',
        header: t('employees.phone'),
        cell: (employee) => (
          employee.phone ? (
            <span className="flex items-center gap-2 text-muted-foreground">
              <Phone className="size-4" />
              {employee.phone}
            </span>
          ) : (
            <span className="text-muted-foreground">-</span>
          )
        ),
      },
      {
        id: 'permissions',
        header: t('employees.permissions'),
        cell: (employee) => (
          employee.permissions && employee.permissions.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {employee.permissions.slice(0, 2).map((perm) => (
                <Badge key={perm} variant="outline" className="text-xs">
                  {perm}
                </Badge>
              ))}
              {employee.permissions.length > 2 && (
                <Badge variant="outline" className="text-xs">
                  +{employee.permissions.length - 2}
                </Badge>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">-</span>
          )
        ),
      },
      {
        id: 'lastLogin',
        header: t('employees.lastLogin'),
        cell: (employee) => (
          employee.lastLoginAt ? (
            <span className="text-sm text-muted-foreground">
              {formatDate(employee.lastLoginAt)}
            </span>
          ) : (
            <span className="text-muted-foreground">-</span>
          )
        ),
      },
      {
        id: 'status',
        header: t('common.status'),
        cell: (employee) => (
          <Badge variant={statusVariantMap[employee.status]}>
            {t(`employees.status.${employee.status}`)}
          </Badge>
        ),
      },
    ],
    [t]
  );

  // Card renderer for mobile view
  const renderCard = (employee: Employee, index: number) => (
    <Card
      key={employee.id || `employee-${index}`}
      className="cursor-pointer hover:border-primary/50 transition-colors"
      onClick={() => handleRowClick(employee)}
    >
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Avatar className="size-12">
              <AvatarImage src={employee.avatar} />
              <AvatarFallback>
                {employee.firstName[0]}
                {employee.lastName[0]}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium">
                {employee.firstName} {employee.lastName}
              </p>
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${roleColors[employee.role]}`}>
                {t(`employees.roles.${employee.role}`)}
              </span>
            </div>
          </div>
          <Badge variant={statusVariantMap[employee.status]}>
            {t(`employees.status.${employee.status}`)}
          </Badge>
        </div>
        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Mail className="size-4" />
            <span>{employee.email}</span>
          </div>
          {employee.phone && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Phone className="size-4" />
              <span>{employee.phone}</span>
            </div>
          )}
        </div>
        {employee.permissions && employee.permissions.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {employee.permissions.slice(0, 3).map((perm) => (
              <Badge key={perm} variant="outline" className="text-xs">
                {perm}
              </Badge>
            ))}
            {employee.permissions.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{employee.permissions.length - 3}
              </Badge>
            )}
          </div>
        )}
        {employee.lastLoginAt && (
          <p className="mt-2 text-xs text-muted-foreground">
            {t('employees.lastLogin')}: {formatDate(employee.lastLoginAt)}
          </p>
        )}
      </CardContent>
    </Card>
  );

  // Card skeleton for loading state
  const renderCardSkeleton = () => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-4">
          <Skeleton className="size-12 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('employees.error')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <SearchBar
          onSearch={setSearch}
          placeholder={t('employees.searchPlaceholder')}
          className="sm:max-w-xs"
        />
        <Link to="/employees/new">
          <Button>
            <Plus className="size-4 mr-2" />
            {t('employees.addEmployee')}
          </Button>
        </Link>
      </div>

      <ResponsiveDataList
        data={employees}
        columns={columns}
        renderCard={renderCard}
        renderCardSkeleton={renderCardSkeleton}
        isLoading={isLoading || companyLoading}
        onRowClick={handleRowClick}
        keyExtractor={(employee, index) => employee.id || `employee-${index}`}
        cardGridClassName="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
        emptyState={
          <EmptyState
            title={t('employees.emptyTitle')}
            description={t('employees.emptyDescription')}
            action={
              <Link to="/employees/new">
                <Button>
                  <Plus className="size-4 mr-2" />
                  {t('employees.addEmployee')}
                </Button>
              </Link>
            }
          />
        }
      />
    </div>
  );
}
